import { U21SelectOptionProps } from 'app/shared/u21-ui/components';
import {
  ConstantValue,
  DurationValue,
  FieldValue,
  MatchlistFieldValue,
  NullValue,
  ValueTypes,
  VariableValue,
} from 'app/modules/rulesAdvanced/models';
import { Unit21DataClassifier } from 'app/modules/dataSettings/responses';
import { ConditionOperandValue } from 'app/modules/detectionModels/components/DropdownBuilder/models';

export type SymbolFieldValue = FieldValue & {
  label: string;
};

// NOTE: this type will need these values until we know legacy rules won't break.
export enum OperandTypes {
  STRUCT = '!struct',
  TEXT = 'text',
  NUMBER = 'number',
  SELECT = 'select',
  MULTISELECT = 'multiselect',
  DATE = 'date',
  DATETIME = 'datetime',
  BOOLEAN = 'boolean',
  ENUM = 'enum',
  LIST = 'list',
}

export type OperandValue =
  | ConstantValue
  | DurationValue
  | FieldValue // NOTE: for backwards compatibility
  | NullValue
  | MatchlistFieldValue
  | SymbolFieldValue
  | (VariableValue & { datatype: string });

export type Operand = {
  operandType: OperandTypes;
  value: OperandValue; // NOTE: we can now support other types like MatchlistValue in the future
  unit21DataClassifier?: Unit21DataClassifier;
  dropdownValues?: U21SelectOptionProps[]; // NOTE: for FE
};

export type ConditionOperand = {
  operandType: OperandTypes;
  value: ConditionOperandValue;
  unit21DataClassifier?: Unit21DataClassifier;
  dropdownValues?: U21SelectOptionProps[];
};

export type FieldValueOperand = {
  operandType: OperandTypes;
  value: FieldValue;
  unit21DataClassifier?: Unit21DataClassifier;
  dropdownValues?: U21SelectOptionProps[];
};

export type VariableValueOperand = {
  operandType: OperandTypes;
  value: VariableValue;
};

export type LeftOperand = FieldValueOperand | VariableValueOperand | null;

export interface DropdownSymbolValue {
  operandType: OperandTypes;
  unit21DataClassifier: `${Unit21DataClassifier}`;
  value: {
    model: string;
    field: string;
    label: string;
    type: ValueTypes.FIELD;
    datatype: Exclude<OperandTypes, 'text'> | 'string';
  };
  dropdownValues: U21SelectOptionProps<string>[];
}

export type OperandSymbol = U21SelectOptionProps & {
  type: OperandTypes;
  value: FieldValueOperand | VariableValueOperand;
};

export type FactId = { type: 'fact_id'; value?: '' };
type SymbolValue = SymbolFieldValue | VariableValue | FactId | OperandSymbol;

export interface BasicSymbolTable {
  [name: string]: OperandSymbol;
}

export interface SymbolTable {
  [name: string]: SymbolValue;
}

// Since OperandSymbol doesn't have one single string for its type, we need to check what it's not.
const isBasicSymbolTable = (
  symbolTable: SymbolTable,
): symbolTable is BasicSymbolTable => {
  return Object.values(symbolTable).every(
    (value) =>
      value.type !== 'FIELD' &&
      value.type !== 'fact_id' &&
      value.type !== 'VARIABLE',
  );
};

export const assertIsBasicSymbolTable: (
  symbolTable: SymbolTable,
) => asserts symbolTable is BasicSymbolTable = (symbolTable) => {
  if (!isBasicSymbolTable(symbolTable))
    throw new Error('Symbol table is not a basic symbol table.');
};
